<template>
  <expansion-panel
    key="expansion-panel-motorbike-data"
    :title="t('offer-manager.create.step-motorbike-data.title')"
    :readonly="panelReadonly"
    :hideDivider="panelHideDivider">
    <template v-slot:panel-content>
      <m-form-row :label="t('offer-manager.create.step-motorbike-data.lbl-model')">
        <m-text-field
          v-if="isEditing && !selectedModel && vehicleModelName"
          :value="vehicleModelName"
          :disabled="true" />
        <m-select
          v-else
          autofocus
          return-object
          data-cy="offerVehicleModelName"
          itemText="description"
          v-model="selectedModel"
          :placeholder="t('dealer-bike.registration.lbl-model-placeholder')"
          :items="models"
          :rules="modelRules"
          :disabled="isModelsLoading || isMaterialsLoading || isModelsSummaryLoading || disabled"
          :loading="isModelsLoading" />
      </m-form-row>

      <m-form-row :label="t('offer-manager.create.step-motorbike-data.lbl-description')">
        <m-text-field
          v-if="isEditing && !selectedModel && vehicleModelSubname"
          :value="vehicleModelSubname"
          :disabled="true" />
        <m-select
          v-else
          autofocus
          return-object
          data-cy="offerVehicleModelSubname"
          itemText="description"
          v-model="selectedMaterial"
          :placeholder="t('offer-manager.create.step-motorbike-data.lbl-description-placeholder')"
          :items="materials"
          :rules="modelDescriptionRules"
          :disabled="!selectedModel || isMaterialsLoading || isModelsSummaryLoading || disabled"
          :loading="isMaterialsLoading" />
      </m-form-row>

      <m-form-row :label="t('offer-manager.create.step-motorbike-data.lbl-rate-price')">
        <price-field
          data-cy="offerVehiclePriceRate"
          :value="vehicleModelPriceRate"
          placeholder="0.00"
          :readonly="true"
          :disabled="disabled"
          :loading="isModelsSummaryLoading" />
      </m-form-row>
      <m-form-row v-if="haveCO2Rates" :label="t('offer-manager.create.step-motorbike-data.lbl-price-ccaa') + '*'">
        <price-field
          data-cy="offerVehiclePriceRateCCAA"
          :value="vehiclePriceRateCCAA"
          :placeholder="t('offer-manager.create.step-motorbike-data.lbl-price-ccaa')"
          @input="handleCO2RateChange"
          @change="handleCO2RateChange"
          :rules="priceRateCCAARules"
          :disabled="!vehicleModelPriceRate || isModelsSummaryLoading || disabled"
          :loading="isModelsSummaryLoading" />
      </m-form-row>

      <mapit-dialog :dialog="confirmEditing" @close="confirmEditing = false">
        <template v-slot:header-icon>
          <v-icon class="text-orange" size="48">fa-solid fa-octagon-xmark</v-icon>
        </template>
        <template v-slot:header-text>
          <span>{{ $t(`offer-manager.dialog-deprecated-catalog.title`) }}</span>
        </template>
        <template v-slot:body>
          <div
            class="text-center"
            v-balance-text.children
            v-html="$t(`offer-manager.dialog-deprecated-catalog.description`)" />
        </template>
        <template v-slot:footer-center>
          <v-btn
            class="btn-gray btn-rounded"
            data-cy="registration-popup-btn-cancel"
            text
            @click="confirmEditing = false"
            >{{ $t('offer-manager.dialog-deprecated-catalog.cancel') }}
          </v-btn>
          <v-btn class="btn-primary btn-rounded" data-cy="registration-popup-btn-exit" text @click="handleEnableEditing"
            >{{ $t('offer-manager.dialog-deprecated-catalog.confirm') }}
          </v-btn>
        </template>
      </mapit-dialog>
    </template>
  </expansion-panel>
</template>

<script lang="ts">
import {computed, defineComponent, inject, Ref, ref, watch} from '@vue/composition-api'

import MapitDialog from '@/components/dialog/MapitDialog.vue'
import ExpansionPanel from '@/components/general/ExpansionPanel.vue'
import PriceField from '@/components/general/PriceField.vue'
import {useCO2Rates} from '@/composables'
import {useMaterials} from '@/composables/materials'
import {
  // @ts-ignore
  modelDescriptionRules,
  modelRules,
  priceRateCCAARules,
} from '@/helpers/offer-manager-rules'
import {useI18n} from '@/i18n'
import {MaterialWithDescription, ModelWithDescription} from '@/types'
import {InputTypes} from '@/utils/constants'
import {mapGetters} from '@/utils/map-store'

export default defineComponent({
  name: 'MotorbikeData',
  components: {ExpansionPanel, MapitDialog, PriceField},
  props: {
    hideDetails: Boolean,
    panelReadonly: Boolean,
    panelHideDivider: Boolean,
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, {root, emit}) {
    const {t} = useI18n()

    const mainLoading = inject('mainLoading') as Ref<boolean>
    const isEditing = inject('isEditing') as Ref<boolean>
    const models = inject('models') as Ref<ModelWithDescription[]>
    const isModelsLoading = inject('isModelsLoading') as Ref<boolean>
    const isModelsSummaryLoading = inject('isModelsSummaryLoading') as Ref<boolean>
    const getSummaryByIdMaterial = inject('getSummaryByIdMaterial') as (
      idMaterial: string | number,
    ) => MaterialWithDescription

    const needsCleaning = ref(true)
    const confirmEditing = ref(false)
    const abortController = ref<AbortController>(new AbortController())

    const {
      getVehicleModelName: vehicleModelName,
      getVehicleModelSubname: vehicleModelSubname,
      getVehicleModelPriceRate: vehicleModelPriceRate,
      getVehiclePriceRateCCAA: vehiclePriceRateCCAA,
      getVehicleDiscountPriceRate: vehicleDiscountPriceRate,
      getVehicleDiscountPriceRatePercent: vehicleDiscountPriceRatePercent,
      getVehicleTotalPrice: vehicleTotalPrice,
      getBranch: branch,
      getOffer: offer,
      isOfferLoading,
    } = mapGetters(root.$store.getters)

    const listCampaigns = ref([
      {text: 'Campaña Summer', value: '#0001'},
      {text: 'Campaña Winter', value: '#0002'},
      {text: 'Campaña Black friday', value: '#0003'},
    ])

    const selectedModel = ref() as Ref<ModelWithDescription | undefined>
    const selectedMaterial = ref() as Ref<MaterialWithDescription | undefined>

    const {materials, loading: isMaterialsLoading} = useMaterials({
      branch: branch.value,
      model: selectedModel,
    })

    const {
      rates,
      selectedRate,
      loading: isCO2RateLoading,
    } = useCO2Rates({
      branch: branch.value,
      material: selectedMaterial,
    })

    const haveCO2Rates = computed(() => Object.keys(rates.value).length > 0)

    const isDoneMainLoading = computed(() => {
      return !mainLoading?.value && !isOfferLoading?.value && !isModelsLoading?.value
    })

    watch(isDoneMainLoading, isDoneMainLoading => {
      if (!isDoneMainLoading) return

      if (!selectedModel.value && vehicleModelName.value && models.value?.length) {
        const alreadySelectedModel = models.value?.find(model => model.description === vehicleModelName.value)

        if (alreadySelectedModel) {
          selectedModel.value = alreadySelectedModel
          needsCleaning.value = false
        }
      }
    })

    watch(selectedModel, value => {
      if (needsCleaning.value) root.$store.dispatch('cleanPacksAndServices')

      updateOffer('setVehicleModelName', value?.description)
      selectedMaterial.value = undefined

      needsCleaning.value = true
    })

    watch(selectedMaterial, async (material, prev) => {
      if (needsCleaning.value) root.$store.dispatch('cleanPacksAndServices')

      if (material) {
        const summary = await getSummaryByIdMaterial(material.idMat)
        updateOffer('setVehicleModelBySubname', {
          summary,
          model: selectedModel.value,
          material,
          rates: rates.value,
          needsUpdate: needsCleaning.value,
          isEditing: isEditing.value,
        })
      } else {
        updateOffer('setVehicleModelBySubname', undefined)
      }
      if (prev) updateOffer('setCampaignClean', undefined)

      needsCleaning.value = true
    })

    watch(materials, newMaterialsValue => {
      if (isEditing.value && !selectedMaterial.value && vehicleModelSubname.value && newMaterialsValue?.length) {
        const alreadySelectedMaterials = newMaterialsValue?.find(
          material => material.description === vehicleModelSubname.value,
        )

        if (alreadySelectedMaterials) {
          selectedMaterial.value = alreadySelectedMaterials
          needsCleaning.value = false
        }
      } else if (newMaterialsValue.length === 1) {
        selectedMaterial.value = newMaterialsValue[0]
      }
    })

    watch(rates, value => {
      root.$store.dispatch('setCatalogCCAARatesOptions', value)
    })

    function updateOffer(action: string, param: any) {
      root.$store.dispatch(action, param)
      validateForm()
    }

    function validateForm() {
      emit('validateForm')
    }

    function handleCO2RateChange(value: string) {
      updateOffer('setVehiclePriceRateCCAA', value)
    }

    function handleEnableEditing() {
      confirmEditing.value = false
    }

    return {
      t,
      offer,
      confirmEditing,
      InputTypes,
      modelRules,
      modelDescriptionRules,
      priceRateCCAARules,
      vehicleModelName,
      vehicleModelSubname,
      vehicleModelPriceRate,
      listCampaigns,
      vehiclePriceRateCCAA,
      vehicleDiscountPriceRate,
      vehicleDiscountPriceRatePercent,
      vehicleTotalPrice,
      models,
      isModelsLoading,
      materials,
      isMaterialsLoading,
      selectedModel,
      selectedMaterial,
      isModelsSummaryLoading,
      selectedRate,
      isCO2RateLoading,
      rates,
      isEditing,
      haveCO2Rates,
      handleCO2RateChange,
      updateOffer,
      validateForm,
      handleEnableEditing,
    }
  },
})
</script>
